import { Box, Divider, Flex, Text } from "@chakra-ui/react";
import { langStore, translate, useSelecting } from "@noli/business";
import { observer } from "mobx-react-lite";
import React, { useEffect, useMemo, useState } from "react";
import { useMount, useUnmount } from "ahooks";

import { CapsulesPanel } from "@/components/Capsules";
import { SubWithTitle } from "@/components/SubPanel/WithTitle";
import TextArea from "@/components/TextArea";
import TextTrigger from "@/components/Triggers/TextTrigger";
import { GenerateRecommendations } from "@/modules/editor/components/common/GenerateRecommendation";
import { ModelSwitch } from "@/modules/editor/components/common/ModelSwitch";
import { SizeSelector } from "@/modules/editor/components/common/SizeSelector";
import { Txt2ImgSubmitButton } from "@/modules/editor/components/common/Txt2ImgSubmitButton";
import { InfoText, LabelText } from "@/modules/editor/components/styleComponents/Texts";
import { WenxinSubmitButton } from "@/thirdParty/wenxin/components/SubmitButton";
import { StyleSelector } from "@/modules/editor/components/common/StyleSelector";
import { ArtistSelector } from "@/modules/editor/components/common/ArtistSelector";
import { CircularSwitch } from "@/modules/editor/components/common/CircularSwitch";
import { ModeSwitch } from "@/modules/editor/components/common/ModeSwitch";
import { WordTable } from "@/modules/editor/components/WordTable";

import {
  MODEL_TYPES_INFO,
  MODEL_TYPES_WITH_THIRD_TYPES,
  MODEL_WORD_TABLE_SOURCES,
  ModeTypes,
  ModelTypes,
  ModelTypesWithThirdParty,
  NoliboxModelStatusNameEnum,
  NoliboxModelTypeEnum,
  defaultOptimizers,
  modelDefaultOptimizers,
  optimizersMap,
} from "@/modules/editor/constant";
import {
  setAdvanceModeNegativePrompt,
  setAdvancePrompt,
  setGenerateCount,
  setGuidanceScale,
  setMode,
  setModelType,
  setNumSteps,
  setOptimizers,
  setSampler,
  setSeed,
  setSelectedThirdPartyModel,
  setThirdPartyModel,
  setTxt2ImgArtist,
  setTxt2ImgStyle,
  setTxt2ImgTexts,
  setUseNegativePrompt,
  setWenxinStyle,
  statusStore,
  updateAnimeSmallModels,
  updatePromptSettings,
  updateSmallModels,
} from "@/stores/status";
import { themeStore } from "@/stores/theme";
import { loginStore } from "@/modules/login/store";

import { Editor_Words } from "@/utils/lang/editor";
import { getTxt2imgGenerateSize, useOwnToast } from "@/utils/misc";
import { NegativePromptSwitch } from "@/modules/editor/components/NegativePromptConfig";
import { NegativePromptWordTable } from "@/modules/editor/components/NegativePromptTable";
import { SizeInput } from "@/modules/editor/components/common/SizeInput";
import { OptimizerSelector } from "@/modules/editor/components/common/OptimizerSelector";
import { getAdvanceModeMetaData } from "@/modules/editor/utils/meta";
import { wordRecordSubmitFilter } from "@/modules/editor/components/WordTable/utils";
import { wordTableStore } from "@/modules/editor/components/WordTable/store";
import { NSlider } from "@/components/NSlider";
import { SamplerSelector } from "../../../components/SamplerSelector";
import { InputField } from "@/modules/editor/components/ParamInputField";
import { allWenxinStyles } from "@/thirdParty/wenxin/types";
import { isThirdParty } from "@/thirdParty/constants";
import { Wenxin_Words } from "@/thirdParty/wenxin/lang";
import MonkeyPanel from "@/thirdParty/monkey/components/MonkeyPanel";
import DraftPanel from "@/thirdParty/draft/components/DraftPanel";
import { GenMultiInput } from "@/modules/editor/components/common/GenMultiInput";
import { SmallModelsSelector } from "@/modules/editor/components/SmallModelsSelector";
import { isAuthUser, isVip } from "@/utils/status";
import { isNoliboxModel } from "@/modules/editor/components/common/ModelSwitch/util";
import {
  AI_MAX_WH,
  DEFAULT_NEGATIVE_PROMPT_EMBEDDING,
  HIGH_RES_ADVANCE_MODE_MAX_WH,
} from "@/utils/constants";
import events, { MessageType } from "@/utils/events";
import { ReactComponent as robot } from "@/assets/icons/animate_robot.svg";
import Icon from "@/components/Icon";
import { monkeyStore } from "@/thirdParty/monkey/store";
import { HighResSwitch } from "@/modules/editor/components/common/HighResSwitch";

const wenxinSubmitSize = "1024*1024";
type RenderConfigType = {
  content: boolean;
  recommendation: boolean;
  style: boolean;
  artist: boolean;
  sizeSelector: boolean;
  circular: boolean;
  prompt: boolean;
  promptWordTable: boolean;
  useNegativePrompt: boolean;
  negativePrompt: boolean;
  advanceModeNegativePrompt: boolean;
  negativePromptWordTable: boolean;
  guidance_scale: boolean;
  num_steps: boolean;
  sampler: boolean;
  seed: boolean;
  sizeInput: boolean;
  optimizer: boolean;
  generateCount: boolean;
  smallModels: boolean;
  highRes?: boolean;
};

const updateSmallModelsMethod = {
  nolibox: updateSmallModels,
  anime: updateAnimeSmallModels,
  dreamlike: updateSmallModels,
  "deliberate-general": updateSmallModels,
  "epicrealisim-real": updateSmallModels,
  "revanime-3d-anime": updateAnimeSmallModels,
};

const Txt2Img: React.FC = () => {
  const t = useOwnToast();
  const lang = langStore.tgt;
  const isGuest = loginStore.isGuest;
  const {
    wenxinStyle,
    modelType,
    mode,
    txt2imgTexts: text,
    txt2imgSize,
    promptExpanding,
  } = statusStore;
  const { panelBg, separatorColor } = themeStore.styles;
  const info = useSelecting("basic")({ fixed: 0 });
  const { displayNode } = info ?? {};
  const alias = displayNode?.alias ?? ".txt2img";
  const showSizeSelectorMessage = displayNode?.type === "rectangle";
  const [needSr, setNeedSr] = useState(!!isAuthUser());
  const [circular, setCircular] = useState(false);
  const config: Record<ModeTypes, Partial<RenderConfigType>> = useMemo(
    () => ({
      simple: {
        content: true,
        recommendation: true,
        style: true,
        artist: true,
        smallModels: isNoliboxModel(modelType) && isAuthUser(),
        optimizer: true,
        sizeSelector: true,
        circular: isNoliboxModel(modelType) && modelType !== "anime",
        generateCount: true,
        highRes: isAuthUser(),
      },
      advance: {
        prompt: true,
        promptWordTable: true,
        useNegativePrompt: true,
        advanceModeNegativePrompt: true,
        negativePromptWordTable: true,
        guidance_scale: true,
        num_steps: true,
        sampler: true,
        smallModels: isNoliboxModel(modelType) && isAuthUser(),
        seed: true,
        sizeInput: true,
        circular: isNoliboxModel(modelType) && modelType !== "anime",
        generateCount: true,
        highRes: isAuthUser(),
      },
    }),
    [modelType, isAuthUser()],
  );
  const renderConfig = useMemo(() => {
    return config[mode];
  }, [mode, modelType]);

  const { w, h } = useMemo(() => {
    const result = {
      w: 0,
      h: 0,
    };
    if (renderConfig.sizeInput) {
      Object.assign(result, statusStore.txt2imgCustomSize);
    } else {
      Object.assign(
        result,
        showSizeSelectorMessage && displayNode
          ? { w: info?.w ?? 0, h: Math.abs(info?.h ?? 0) }
          : getTxt2imgGenerateSize(txt2imgSize),
      );
    }

    if (needSr) {
      result.w = result.w * 2;
      result.h = result.h * 2;
    }
    return result;
  }, [
    txt2imgSize,
    showSizeSelectorMessage,
    displayNode,
    info,
    renderConfig.sizeInput,
    statusStore.txt2imgCustomSize,
    needSr,
  ]);

  useEffect(() => {
    if (modelType === "anime" && circular) {
      setCircular(false);
    }
  }, [modelType]);

  useMount(() => {
    if (!isAuthUser() && isThirdParty(modelType)) {
      setModelType("deliberate-general");
    }
    if (modelType == "nolibox") {
      setModelType("deliberate-general");
    }
  });
  useUnmount(() => {
    if (isThirdParty(modelType)) {
      setModelType("deliberate-general");
      monkeyStore.setSelectedModel();
    }
  });

  function handleModelChange(modelType: ModelTypesWithThirdParty) {
    setModelType(modelType);
    if (modelType !== "third-party") {
      setOptimizers(modelDefaultOptimizers[modelType] || defaultOptimizers);
    }
  }

  const setIsAnime = (isAnime: boolean) => {
    handleModelChange(isAnime ? "anime" : "deliberate-general");
  };

  const submitProps = useMemo(() => {
    const highres_info = {
      upscale_factor: 2,
    };
    if (mode === "advance" && !isThirdParty(modelType)) {
      const promptKeyValueMap =
        wordTableStore[MODEL_WORD_TABLE_SOURCES[modelType].name].keyValueMap;
      const negativePromptKeyValueMap = wordTableStore.negativePromptKeywords.keyValueMap;

      return {
        originalNegative: statusStore.advanceModeNegativePrompt,
        text: statusStore.advancePrompt,
        txt2ImgCustomSize: statusStore.txt2imgCustomSize,
        generateMetaFn: getAdvanceModeMetaData,
        extraPromptInfo: {
          promptSettings: statusStore.promptSettings?.filter((record) =>
            wordRecordSubmitFilter(statusStore.advancePrompt, record, promptKeyValueMap),
          ),
          negativePromptSettings: statusStore.negativePromptSettings?.filter((record) =>
            wordRecordSubmitFilter(
              statusStore.advanceModeNegativePrompt,
              record,
              negativePromptKeyValueMap,
            ),
          ),
        },
        highResInfo: needSr ? highres_info : undefined,
        extraAdvanceMetaData: {
          guidance_scale: statusStore.guidance_scale,
          num_steps: statusStore.num_steps,
          sampler: statusStore.sampler,
          seed: statusStore.seed,
        },
      };
    }

    const originNegative = statusStore.optimizers?.length
      ? statusStore.optimizers.map((optimizeType) => optimizersMap[optimizeType].value).join(", ")
      : "";
    return {
      text,
      originalNegative: originNegative,
      highResInfo: needSr ? highres_info : undefined,
    };
  }, [
    mode,
    text,
    statusStore.advanceModeNegativePrompt,
    statusStore.advancePrompt,
    statusStore.txt2imgCustomSize,
    getAdvanceModeMetaData,
    statusStore.promptSettings,
    statusStore.negativePromptSettings,
    statusStore.optimizers,
    statusStore.guidance_scale,
    statusStore.num_steps,
    statusStore.sampler,
    statusStore.seed,
    needSr,
    isVip(),
  ]);

  const submitCustomEmbeddings = useMemo(() => {
    const models =
      modelType === "anime"
        ? statusStore.animeSmallModels.concat()
        : statusStore.smallModels.concat();
    models.push(DEFAULT_NEGATIVE_PROMPT_EMBEDDING);
    return models;
  }, [modelType, statusStore.animeSmallModels, statusStore.smallModels]);

  return (
    <SubWithTitle
      minH="100%"
      flex={1}
      title={translate(Editor_Words["txt2img-title"], lang)}
      pb="24px"
    >
      <ModelSwitch
        enableModelTypes={
          !isAuthUser()
            ? MODEL_TYPES_WITH_THIRD_TYPES.filter((type) => !isThirdParty(type))
            : MODEL_TYPES_WITH_THIRD_TYPES
        }
        onChange={handleModelChange}
        direction="column"
        mt="16px"
      />
      {isNoliboxModel(modelType) ? <ModeSwitch value={mode} onChange={setMode} mt="16px" /> : null}
      <Divider borderColor={separatorColor} w="100%" m="16px 0" />
      {modelType === "wenxin" && !isGuest ? (
        <Box w="100%" pos="relative">
          <LabelText display="flex" justifyContent="space-between">
            <Text>{translate(Editor_Words["prompt-for-img"], lang)}：</Text>
            <Text pos="relative" onClick={() => events.emit(MessageType.PROMPT_EXPAND)}>
              <Text
                border="1px solid"
                pos="relative"
                px="10px"
                bg="#323232"
                borderRadius="4px"
                color="#b3b3b3"
                cursor="pointer"
                zIndex={1}
                _hover={{ color: "white" }}
              >
                {translate(Editor_Words["prompt-expand-modal-title"], lang)}
              </Text>
              <Text
                pos="absolute"
                px="6px"
                right={0}
                bottom="100%"
                bg="#36C19E"
                color="white"
                borderRadius="3px"
                fontSize="12px"
                _after={{
                  content: "''",
                  bg: "#36c19e",
                  w: "10px",
                  h: "10px",
                  pos: "absolute",
                  right: 0,
                  top: "80%",
                }}
              >
                new
              </Text>
            </Text>
          </LabelText>

          <Box h="fit-content" pos="relative" my="16px">
            <TextArea
              w="100%"
              my="0px"
              lang={lang}
              textAreaH={300}
              minRows={5}
              maxRows={10}
              placeholderWord={Editor_Words["textarea-placeholder"]}
              text={statusStore.txt2imgTexts}
              setText={setTxt2ImgTexts}
            />
            {promptExpanding && (
              <Flex
                w="100%"
                h="100%"
                pos="absolute"
                top="0"
                left="0"
                flexGrow={1}
                bg="#323232"
                borderRadius="6px"
                border="1px solid white"
                justify="center"
                flexDir="column"
                align="center"
              >
                <Icon w="32px" h="32px" svg={robot} />
                <Text color="#b3b3b3">提示词生成中</Text>
              </Flex>
            )}
          </Box>
          <TextTrigger
            mt="-6px"
            panelTitle={translate(Editor_Words["txt2img-sd-style-title"], lang)}
            storeValue={wenxinStyle}
            setStoreValue={setWenxinStyle}
            popupGenerator={(value, setValue) => (
              <CapsulesPanel
                w="200px"
                p="12px"
                h={250}
                bg={panelBg}
                words={allWenxinStyles}
                callbackFn={(style) => async () => setValue(style)}
                focusingWord={value}
              />
            )}
            safeDistance={250 - 60}
            smartAlign={{
              offset: [24, 0],
              points: ["bl", "br"],
            }}
          />

          <InfoText>
            {translate(Editor_Words["model"], lang)}：
            {modelType ? translate(MODEL_TYPES_INFO[modelType]?.showInfo, lang) : ""}
          </InfoText>
          <InfoText mt="12px">
            {translate(Wenxin_Words["wenxin-resolution"], lang)}
            {wenxinSubmitSize.split("*").join(" x ")} px
          </InfoText>
          <WenxinSubmitButton
            mt="48px"
            t={t}
            lang={lang}
            opt={{
              text,
              size: wenxinSubmitSize,
              style: wenxinStyle,
            }}
          />
        </Box>
      ) : null}
      {modelType === "monkey" ? <MonkeyPanel /> : null}
      {modelType === "draft" ? <DraftPanel /> : null}

      {isNoliboxModel(modelType) ? (
        <Box w="100%">
          {renderConfig.content && (
            <LabelText display="flex" justifyContent="space-between">
              <Text>{translate(Editor_Words["prompt-for-img"], lang)}：</Text>
              <Text pos="relative" onClick={() => events.emit(MessageType.PROMPT_EXPAND)}>
                <Text
                  border="1px solid"
                  pos="relative"
                  px="10px"
                  bg="#323232"
                  borderRadius="4px"
                  color="#b3b3b3"
                  cursor="pointer"
                  zIndex={1}
                  _hover={{ color: "white" }}
                >
                  {translate(Editor_Words["prompt-expand-modal-title"], lang)}
                </Text>
                <Text
                  pos="absolute"
                  px="6px"
                  right={0}
                  bottom="100%"
                  bg="#36C19E"
                  color="white"
                  borderRadius="3px"
                  fontSize="12px"
                  _after={{
                    content: "''",
                    bg: "#36c19e",
                    w: "10px",
                    h: "10px",
                    pos: "absolute",
                    right: 0,
                    top: "80%",
                  }}
                >
                  new
                </Text>
              </Text>
            </LabelText>
          )}
          {renderConfig.prompt && (
            <LabelText display="flex" justifyContent="space-between">
              <Text>{translate(Editor_Words["prompt-input-title"], lang)}：</Text>
              <Text pos="relative" onClick={() => events.emit(MessageType.PROMPT_EXPAND)}>
                <Text
                  border="1px solid"
                  pos="relative"
                  px="10px"
                  bg="#323232"
                  borderRadius="4px"
                  color="#b3b3b3"
                  cursor="pointer"
                  zIndex={1}
                  _hover={{ color: "white" }}
                >
                  {translate(Editor_Words["prompt-expand-modal-title"], lang)}
                </Text>
                <Text
                  pos="absolute"
                  px="6px"
                  right={0}
                  bottom="100%"
                  bg="#36C19E"
                  color="white"
                  borderRadius="3px"
                  fontSize="12px"
                  _after={{
                    content: "''",
                    bg: "#36c19e",
                    w: "10px",
                    h: "10px",
                    pos: "absolute",
                    right: 0,
                    top: "80%",
                  }}
                >
                  new
                </Text>
              </Text>
            </LabelText>
          )}

          {renderConfig.content && (
            <Box h="fit-content" pos="relative" my="16px">
              <TextArea
                w="100%"
                m="0"
                lang={lang}
                textAreaH={300}
                minRows={5}
                maxRows={10}
                placeholderWord={Editor_Words["textarea-placeholder"]}
                text={statusStore.txt2imgTexts}
                setText={setTxt2ImgTexts}
              />
              {promptExpanding && (
                <Flex
                  w="100%"
                  h="100%"
                  pos="absolute"
                  top="0"
                  left="0"
                  flexGrow={1}
                  bg="#323232"
                  borderRadius="6px"
                  border="1px solid white"
                  justify="center"
                  flexDir="column"
                  align="center"
                >
                  <Icon w="32px" h="32px" svg={robot} />
                  <Text color="#b3b3b3">提示词生成中</Text>
                </Flex>
              )}
            </Box>
          )}
          {renderConfig.prompt && (
            <Box h="fit-content" pos="relative" my="16px">
              <TextArea
                w="100%"
                m="0"
                lang={lang}
                textAreaH={300}
                minRows={5}
                maxRows={10}
                placeholderWord={Editor_Words["textarea-placeholder"]}
                text={statusStore.advancePrompt}
                setText={setAdvancePrompt}
              />
              {promptExpanding && (
                <Flex
                  w="100%"
                  h="100%"
                  pos="absolute"
                  top="0"
                  left="0"
                  flexDir="column"
                  flexGrow={1}
                  bg="#323232"
                  borderRadius="6px"
                  border="1px solid white"
                  justify="center"
                  align="center"
                >
                  <Icon w="32px" h="32px" svg={robot} />
                  <Text color="#b3b3b3">提示词生成中</Text>
                </Flex>
              )}
            </Box>
          )}
          {isNoliboxModel(modelType) && renderConfig.promptWordTable && (
            <WordTable
              text={statusStore.advancePrompt}
              setText={setAdvancePrompt}
              title={translate(Editor_Words["prompt-word-table-title"], lang)}
              onSelect={(record) => {
                updatePromptSettings(record);
              }}
              sourceUrl={MODEL_WORD_TABLE_SOURCES[modelType].url}
              searchPlaceholder={Editor_Words["prompt-keyword-search-placeholder"]}
              defaultCategory="people"
              tableRows={4}
              tableName={MODEL_WORD_TABLE_SOURCES[modelType].name}
            />
          )}
          {renderConfig.useNegativePrompt && (
            <NegativePromptSwitch
              value={statusStore.useNegativePrompt}
              onChange={setUseNegativePrompt}
            />
          )}
          {renderConfig.advanceModeNegativePrompt && statusStore.useNegativePrompt && (
            <TextArea
              w="100%"
              mb="16px"
              lang={lang}
              textAreaH={300}
              minRows={5}
              maxRows={10}
              placeholderWord={Editor_Words["negative-prompt-placeholder"]}
              text={statusStore.advanceModeNegativePrompt}
              setText={setAdvanceModeNegativePrompt}
            />
          )}
          {renderConfig.negativePromptWordTable && statusStore.useNegativePrompt && (
            <NegativePromptWordTable />
          )}

          {renderConfig.recommendation && (
            <GenerateRecommendations setIsAnime={setIsAnime} setCircular={setCircular} />
          )}

          {renderConfig.style && (
            <StyleSelector mt="20px" value={statusStore.txt2imgStyle} setValue={setTxt2ImgStyle} />
          )}
          {renderConfig.artist && (
            <ArtistSelector value={statusStore.txt2imgArtist} setValue={setTxt2ImgArtist} />
          )}
          {renderConfig.smallModels && (
            <SmallModelsSelector
              type={NoliboxModelTypeEnum[modelType]}
              value={statusStore[NoliboxModelStatusNameEnum[modelType]]}
              onChange={updateSmallModelsMethod[modelType]}
            />
          )}
          {renderConfig.optimizer && (
            <OptimizerSelector mt="16px" value={statusStore.optimizers} setValue={setOptimizers} />
          )}
          {renderConfig.guidance_scale && (
            <NSlider
              mt="16px"
              value={statusStore.guidance_scale}
              max={30}
              min={-20}
              step={0.5}
              label={translate(Editor_Words["guidance-scale-caption"], lang)}
              onChange={(guidance_scale) => {
                setGuidanceScale(guidance_scale);
              }}
            />
          )}
          {renderConfig.num_steps && (
            <NSlider
              mt="16px"
              value={statusStore.num_steps}
              max={100}
              min={5}
              step={1}
              label={translate(Editor_Words["num-steps-caption"], lang)}
              onChange={(num_steps) => {
                setNumSteps(num_steps);
              }}
            />
          )}
          {renderConfig.sampler && (
            <SamplerSelector
              mt="16px"
              value={statusStore.sampler}
              onChange={(sampler) => {
                setSampler(sampler);
              }}
            />
          )}
          {renderConfig.seed && (
            <InputField
              mt="16px"
              lang={lang}
              identifier="advance-mode-seed"
              word={Editor_Words["seed-caption"]}
              value={statusStore.seed ?? -1}
              setValue={(seed) => {
                setSeed(seed);
              }}
              lower={-1}
            />
          )}
          {renderConfig.sizeSelector && (
            <SizeSelector mt="16px" showSizeSelectorMessage={showSizeSelectorMessage} />
          )}
          {renderConfig.sizeInput && (
            <SizeInput
              mt="16px"
              wMin={512}
              hMin={512}
              wMax={isVip() ? HIGH_RES_ADVANCE_MODE_MAX_WH : AI_MAX_WH}
              hMax={isVip() ? HIGH_RES_ADVANCE_MODE_MAX_WH : AI_MAX_WH}
            />
          )}

          {renderConfig.circular && (
            <CircularSwitch alias={alias} circular={circular} onChange={setCircular} />
          )}
          {renderConfig.generateCount && isAuthUser() && (
            <GenMultiInput
              mt="16px"
              value={statusStore.generateCount}
              onChange={setGenerateCount}
              min={1}
              max={100}
            />
          )}
          {renderConfig.highRes && <HighResSwitch value={needSr} onChange={setNeedSr} />}

          <InfoText mt="16px">
            {translate(Editor_Words["model"], lang)}：
            {modelType ? translate(MODEL_TYPES_INFO[modelType]?.showInfo, lang) : ""}
          </InfoText>
          <InfoText mt="12px">
            {translate(Editor_Words["resolution"], lang)}：{w} x {h}px
          </InfoText>
          <Txt2ImgSubmitButton
            size={{ w, h }}
            mt="48px"
            t={t}
            lang={lang}
            circular={circular}
            modelType={modelType}
            defaultBusyAlias=".onSubmitTxt2Img."
            customEmbeddings={submitCustomEmbeddings}
            generateCount={isAuthUser() && isVip() ? statusStore.generateCount : 1}
            {...submitProps}
          />
        </Box>
      ) : null}
    </SubWithTitle>
  );
};

export const Txt2ImgPanel = observer(Txt2Img);
